import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Flame, Wind, Snowflake, Heart, Leaf, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Page, BrandHeader, CloseBtn, Spinner } from "@/components/Brand";
import { Btn } from "@/components/Btn";
import { MoodVortex } from "@/components/MoodVortex";
import { CopingSelector } from "@/components/CopingSelector";
import { NeedPicker, NEED_UNSURE } from "@/components/NeedPicker";
import { SchemaRecognizer } from "@/components/SchemaRecognizer";
import { FlipPlan } from "@/components/FlipPlan";
import {
  COPING_MODE_START, COPING_MODE_TREE, fourFConfig, isModeResult, isSchemaResult, moodLevel,
  SCHEMA_START, SCHEMA_TREE, schemaMeaning, schemaSoothe, modeFourF, modeMeaning, modeSoothe, VALIDATION_TOOL,
} from "@/lib/config";
import { emptyFlip, needStatements } from "@/lib/schemaFlip";
import { healthyWins, healthyWinMessage } from "@/lib/healthyWins";
import { aiPost, useSaveFlip, useSessions, useUpdateSession } from "@/lib/store";
import { buildAiContext } from "@/lib/aiContext";
import { AiNoteActions, NoteHeader } from "@/components/AiNote";
import { NeedAssistant } from "@/components/NeedAssistant";
import { NeedIdeas } from "@/components/NeedIdeas";
import { suggestSchemas } from "@/lib/schemaSuggest";
import { sudsVar } from "@/lib/theme";
import { withCompletion } from "@/lib/sections";

const ICONS = { flame: Flame, wind: Wind, snowflake: Snowflake, heart: Heart };
const LOW_MOOD = -3;

const Q = ({ tag, q, help }) => (
  <>
    {tag ? <p className="tf-tag">{tag}</p> : null}
    {q ? <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-t-on leading-tight" data-testid="reflection-question">{q}</h1> : null}
    {help ? <p className="mt-3 text-t-on2">{help}</p> : null}
  </>
);
const Soothe = ({ text, title = "A moment to soothe", icon: Icon = Leaf, tint }) => (
  <div className={`mt-6 rounded-[20px] p-5 ${tint || "bg-t-brand2"}`} data-testid="soothe-card">
    <p className="flex items-center gap-2 font-semibold text-t-onbrand2"><span className="w-8 h-8 rounded-full bg-t-surface grid place-items-center text-t-brand"><Icon size={16} /></span>{title}</p>
    <p className="mt-3 text-t-on text-[16px] leading-relaxed">{text}</p>
  </div>
);
const YesNo = ({ onYes, onNo }) => (
  <div className="mt-8 flex gap-3">
    <Btn className="flex-1" label="Yes" onClick={onYes} testId="answer-yes" />
    <Btn className="flex-1" variant="secondary" label="No" onClick={onNo} testId="answer-no" />
  </div>
);
const TreeStep = ({ tag, node, onPick, isResult, restart, showRestart, prefix }) => (
  <>
    <Q tag={tag} q={node.question} />
    <div className="mt-8 flex flex-col gap-3">
      {node.options.map((o) => (
        <Btn key={o.label} label={o.label} variant={isResult(o) ? "primary" : "secondary"} onClick={() => onPick(o)} className="w-full" testId={`${prefix}-${node.id}-${o.label.split(" ")[0]}`} />
      ))}
    </div>
    {showRestart ? <button onClick={restart} className="mt-5 text-t-brand font-semibold text-sm" data-testid={`${prefix}-restart`}>Start over</button> : null}
  </>
);

const RECOVERY_MAX_MS = 6 * 3600000;

export default function Reflection() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const start = sp.get("start");
  const only = sp.get("only");
  const navigate = useNavigate();
  const { data: sessions } = useSessions();
  const update = useUpdateSession();
  const saveFlip = useSaveFlip();
  const session = useMemo(() => sessions?.find((s) => s.id === id), [sessions, id]);

  const [step, setStep] = useState(null);
  const [postSuds, setPostSuds] = useState(null);
  const [triggered, setTriggered] = useState(null);
  const [triggeredAt, setTriggeredAt] = useState(null);
  const [triggerNote, setTriggerNote] = useState("");
  const [response, setResponse] = useState(null);
  const [schemaState, setSchemaState] = useState(null);
  const [schemaNodeId, setSchemaNodeId] = useState(SCHEMA_START);
  const [modeState, setModeState] = useState(null);
  const [modeNodeId, setModeNodeId] = useState(COPING_MODE_START);
  const [coping, setCoping] = useState([]);
  const [insight, setInsight] = useState(null);
  const [needs, setNeeds] = useState([]);
  const [needIdeas, setNeedIdeas] = useState([]);
  const [recognized, setRecognized] = useState([]);
  const [flips, setFlips] = useState([]);
  const [notes, setNotes] = useState("");
  const [suggested, setSuggested] = useState([]);
  const [reasons, setReasons] = useState({});
  const [loading, setLoading] = useState(false);
  const [moodBefore, setMoodBefore] = useState(null);
  const [moodNow, setMoodNow] = useState(null);
  const [moodNote, setMoodNote] = useState("");
  const [moodBeforeNote, setMoodBeforeNote] = useState("");
  const [moodSummary, setMoodSummary] = useState(null);
  const [calmAt, setCalmAt] = useState(null);
  const [skipped, setSkipped] = useState([]);
  const skip = (...k) => setSkipped((p) => [...new Set([...p, ...k])]);
  const emaSchemas = recognized.map((r) => r.schema);

  useEffect(() => {
    if (!calmAt && ((postSuds !== null && postSuds <= 3) || (moodNow !== null && moodNow >= 0))) setCalmAt(new Date().toISOString());
  }, [postSuds, moodNow, calmAt]);

  useEffect(() => {
    if (step !== null || sessions === undefined) return;
    if (start === "mood") {
      setTriggered(false);
      setStep("moodnow");
      return;
    }
    if (session?.moodBefore != null) {
      setMoodBefore(session.moodBefore);
      setMoodBeforeNote(session.moodBeforeNote ?? "");
    }
    if (session) {
      setPostSuds(session.postSuds ?? null);
      setTriggered(session.triggered ?? null);
      setTriggeredAt(session.triggeredAt ?? null);
      setTriggerNote(session.triggerNote ?? "");
      setResponse(session.survivalResponse ?? null);
      setSchemaState(session.schemaState ?? null);
      setModeState(session.copingMode ?? null);
      setInsight(session.aiInsight ?? null);
      setNeeds(session.needs ?? []);
      setNotes(session.reflectionNotes ?? "");
      setMoodNow(session.moodNow ?? null);
      setMoodNote(session.moodNote ?? "");
      setSkipped(session.skipped ?? []);
      setNeedIdeas(session.needIdeas ?? []);
    }
    if (only) return setStep("only");
    setStep(session?.path === "emergency" ? "post" : "triggered");
  }, [sessions, session, step, start, only]);

  useEffect(() => {
    if (step !== "only") return;
    if (!session) return navigate("/history", { replace: true });
    const go = { mode: startMode, schema: startSchema, needs: () => setStep("need"), schemas: goToRecognize, insight: goToInsight }[only];
    go ? go() : navigate(`/entry/${id}`, { replace: true });
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveOnly = async (patch, skipKeys = []) => {
    await update.mutateAsync({ id, patch: withCompletion(session, patch, skipKeys) });
    if (flips.length && only === "schemas") await saveFlip.mutateAsync({ source: "reflection", sessionId: id, needs: needs.filter((n) => n !== NEED_UNSURE), needIdeas, flips });
    toast.success("Section saved");
    navigate(`/entry/${id}`, { replace: true });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const ctx = () => ({
    preSuds: session?.preSuds ?? null, postSuds, survivalResponse: response, schemaState, copingMode: modeState,
    copingMechanisms: coping, triggerNote: triggerNote.trim() || null,
  });
  const fullCtx = (extra = {}) => buildAiContext({
    path: session?.path, emergencySeconds: session?.emergencySeconds, moodOnly: start === "mood",
    when: new Date(session?.timestamp ?? Date.now()).toLocaleString([], { weekday: "long", hour: "numeric", minute: "2-digit" }),
    preSuds: session?.preSuds, postSuds, moodBefore, moodBeforeNote: moodBeforeNote.trim(), triggered, triggeredAt,
    triggerNote: triggerNote.trim(), survivalResponse: response, copingMode: modeState, schemaState,
    coping: [...new Set([...(session?.copingMechanisms ?? []), ...coping])].filter((c) => c !== VALIDATION_TOOL),
    ...extra,
  });

  const startValidation = async () => {
    setStep("moodsummary");
    setLoading(true);
    setMoodSummary(null);
    const txt = await aiPost("mood-summary", { context: fullCtx() }, "summary");
    setMoodSummary(txt);
    setLoading(false);
  };

  const finish = async (complete = true) => {
    const eligible = triggered === true || session?.path === "emergency";
    const allCoping = [...new Set([...(session?.copingMechanisms ?? []), ...coping])];
    const fresh = calmAt && new Date(calmAt) - new Date(session?.timestamp ?? 0) <= RECOVERY_MAX_MS;
    const hard = triggered === true || session?.path === "emergency" || (moodNow !== null && moodNow < LOW_MOOD) || (moodBefore !== null && moodBefore < LOW_MOOD);
    await update.mutateAsync({
      id,
      patch: {
        postSuds, triggered, triggeredAt, triggerNote: triggerNote.trim() || null, survivalResponse: response, schemaState,
        copingMode: modeState, copingMechanisms: [...new Set([...(session?.copingMechanisms ?? []), ...coping])], emaSchemas, aiInsight: insight,
        needs: needs.filter((n) => n !== NEED_UNSURE), needIdeas,
        reflectionNotes: notes.trim() || null, moodBefore, moodBeforeNote: moodBeforeNote.trim() || null, moodNow, moodNote: moodNote.trim() || null,
        moodOnly: start === "mood",
        completed: complete || session?.completed === true,
        skipped: complete && triggered && !allCoping.length ? [...new Set([...skipped, "coping"])] : skipped,
        recoveredAt: session?.recoveredAt ?? (eligible && fresh ? calmAt : null),
      },
    });
    let msg = "Entry saved to your history";
    if (flips.length) {
      await saveFlip.mutateAsync({ source: "reflection", sessionId: id, needs: needs.filter((n) => n !== NEED_UNSURE), needIdeas, flips });
      if (flips.some((f) => f.copingStyle === "healthy")) msg = healthyWinMessage(healthyWins([{ timestamp: new Date().toISOString(), flips }]));
    }
    if (!complete && session?.completed !== true) {
      toast.success("Progress saved — finish it anytime from History");
      return navigate("/history", { replace: true });
    }
    toast.success(msg);
    navigate(hard ? "/reach-out" : "/", { replace: true });
  };

  const finishOrNeeds = () => (triggered ? setStep("needask") : finish());

  const getHelpForMood = async () => {
    await update.mutateAsync({ id, patch: { path: "emergency", moodBefore: moodNow, moodBeforeNote: moodNote.trim() || null } });
    navigate(`/emergency/${id}`, { replace: true });
  };

  const onTriggered = (v) => {
    setTriggered(v);
    setTriggeredAt(v ? (triggeredAt ?? new Date().toISOString()) : null);
    setStep(v ? "moodbefore" : "moodnow");
  };
  const startSchema = () => { setSchemaNodeId(SCHEMA_START); setSchemaState(null); setStep("schema"); };
  const startMode = () => { setModeNodeId(COPING_MODE_START); setModeState(null); setStep("mode"); };
  const onSchemaOption = (o) => (isSchemaResult(o) ? (setSchemaState(o.result), setStep("soothe")) : setSchemaNodeId(o.next));
  const onModeOption = (o) => {
    if (!isModeResult(o)) return setModeNodeId(o.next);
    setModeState(o.result);
    setResponse(modeFourF(o.result));
    setStep("moderesult");
  };

  const goToRecognize = async () => {
    setStep("recognize");
    setLoading(true);
    const r = await suggestSchemas(fullCtx({ moodNow, moodNote: moodNote.trim(), needs }));
    setSuggested(r.schemas);
    setReasons(r.reasons);
    setLoading(false);
  };
  const afterRecognize = () => {
    if (!recognized.length && only) return saveOnly({}, ["emaSchemas"]);
    if (!recognized.length) return (skip("emaSchemas"), goToInsight());
    setFlips(recognized.map((r) => {
      const prev = flips.find((f) => f.schema === r.schema);
      return prev ? { ...prev, copingStyle: r.copingStyle } : emptyFlip(r.schema, r.copingStyle);
    }));
    setStep("flip");
  };
  const goToInsight = async () => {
    setStep("insight");
    setLoading(true);
    const txt = await aiPost("insight", { context: fullCtx({ moodNow, moodNote: moodNote.trim(), needs, flips }) }, "insight");
    setInsight(txt || "What you noticed today makes complete sense. These states are old, protective parts of you that once kept you safe. Be gentle with yourself now; you did something brave simply by pausing to understand what happened.");
    setLoading(false);
  };

  const body = () => {
    switch (step) {
      case "post":
        return (
          <>
            <Q q="How's your distress now?" help={`You started at ${session?.preSuds ?? "—"}. Where are you now?`} />
            <div className="mt-8 grid grid-cols-6 sm:grid-cols-11 gap-2">
              {Array.from({ length: 11 }).map((_, n) => (
                <button key={n} onClick={() => setPostSuds(n)} data-testid={`post-suds-${n}`}
                  className="h-14 rounded-2xl border font-bold text-lg transition-colors"
                  style={postSuds === n ? { background: sudsVar(n), borderColor: sudsVar(n), color: "#fff" } : { background: "var(--surface2)", borderColor: "var(--border)", color: "var(--on2)" }}>{n}</button>
              ))}
            </div>
          </>
        );
      case "moodbefore":
        return (
          <>
            <Q tag="Mood check" q="What was your mood as a result of being triggered?" help="Think about how the trigger made you feel. Tap the mood that fits — this helps you see how far you've come." />
            <div className="mt-4"><MoodVortex value={moodBefore} onChange={setMoodBefore} /></div>
            <p className="tf-tag mt-6">A note (optional)</p>
            <input className="tf-input mt-2" value={moodBeforeNote} onChange={(e) => setMoodBeforeNote(e.target.value)} placeholder="e.g. A tense call, felt overwhelmed…" data-testid="moodbefore-note-input" />
          </>
        );
      case "triggered":
        return (<><Q q="Have you been triggered?" help="If yes, we'll log the date and time for your records." /><YesNo onYes={() => onTriggered(true)} onNo={() => onTriggered(false)} /></>);
      case "trignote":
        return (
          <>
            <Q q="What triggered you?" help="Jot a short note if you'd like — it helps patterns become clearer over time. You can also skip this." />
            <textarea className="tf-input mt-6" value={triggerNote} onChange={(e) => setTriggerNote(e.target.value)} placeholder="e.g. A raised voice, a certain memory, feeling ignored…" data-testid="trigger-note-input" />
          </>
        );
      case "identify":
        return (<><Q q="Do you want to identify your coping mode?" help="A few quick yes/no questions will name your coping mode and survival response. You can skip this if you'd rather not." /><YesNo onYes={startMode} onNo={() => { skip("copingMode", "schemaState"); startValidation(); }} /></>);
      case "mode":
        return <TreeStep tag="Coping mode" node={COPING_MODE_TREE[modeNodeId]} onPick={onModeOption} isResult={isModeResult} restart={startMode} showRestart={modeNodeId !== COPING_MODE_START} prefix="mode" />;
      case "moderesult": {
        const cfg = response ? fourFConfig(response) : null;
        const Icon = cfg ? ICONS[cfg.icon] : null;
        const tint = cfg ? `var(--${cfg.key})` : "var(--brand)";
        const track = { fight: "Overcompensator", flight: "Avoidance", freeze: "Surrender" }[response];
        return (
          <>
            <Q tag="Coping mode activated" q={modeState} />
            {cfg ? (
              <span className="mt-4 inline-flex items-center gap-2 rounded-full border px-4 h-10 font-semibold" style={{ color: tint, borderColor: tint, background: `color-mix(in srgb, ${tint} 14%, transparent)` }} data-testid="fourf-badge">
                <Icon size={17} fill="currentColor" /> {cfg.label} response{track ? ` · ${track}` : ""}
              </span>
            ) : null}
            {modeMeaning(modeState) ? <Soothe title="What this means" icon={Sparkles} text={modeMeaning(modeState)} tint="bg-t-brand3" /> : null}
            {modeSoothe(modeState) ? <Soothe text={modeSoothe(modeState)} /> : null}
          </>
        );
      }
      case "schema":
        return <TreeStep tag="Schema state" node={SCHEMA_TREE[schemaNodeId]} onPick={onSchemaOption} isResult={isSchemaResult} restart={startSchema} showRestart={schemaNodeId !== SCHEMA_START} prefix="schema" />;
      case "soothe":
        return (<><Q tag="You identified" q={schemaState} help={schemaMeaning(schemaState)} />{schemaSoothe(schemaState) ? <Soothe text={schemaSoothe(schemaState)} /> : null}</>);
      case "moodsummary":
        return (
          <>
            <Q tag="You are heard" q="What you felt makes sense." />
            <div className="mt-6 tf-card p-5" data-testid="mood-summary">
              <NoteHeader title={<p className="flex items-center gap-2 font-semibold text-t-on2"><Sparkles size={16} className="text-t-brand" /> A word from Triggerific-Pilot</p>} text={!loading ? moodSummary : null} testId="mood-summary-listen" />
              {loading ? <Spinner className="mt-4 text-t-brand" /> : <p className="mt-3 text-t-on leading-relaxed whitespace-pre-line text-[16px]">{moodSummary || "You stayed with yourself through this — that matters."}</p>}
              {!loading && moodSummary ? <AiNoteActions text={moodSummary} kind="mood-summary" sessionId={id} testId="mood-summary" /> : null}
            </div>
          </>
        );
      case "moodnow": {
        const before = moodLevel(moodBefore);
        const now = moodLevel(moodNow);
        const diff = moodBefore != null && moodNow != null ? moodNow - moodBefore : null;
        return (
          <>
            <Q tag="Mood check" q="How do you feel right now?" help="Tap the mood that best matches where you are in this moment." />
            <div className="mt-4"><MoodVortex value={moodNow} onChange={setMoodNow} /></div>
            {diff != null && before && now ? (
              <p className={`mt-4 rounded-2xl px-4 py-3 font-semibold text-t-on ${diff > 0 ? "bg-t-brand2" : "bg-t-surface2"}`} data-testid="mood-delta">
                {before.label} → {now.label} · {diff > 0 ? `+${diff} lift` : diff < 0 ? diff : "no change"}
              </p>
            ) : null}
            <p className="tf-tag mt-6">A note (optional)</p>
            <input className="tf-input mt-2" value={moodNote} onChange={(e) => setMoodNote(e.target.value)} placeholder="e.g. A good chat, a hard morning, slept well…" data-testid="mood-note-input" />
          </>
        );
      }
      case "needask":
        return (<><Q tag="Underlying needs" q="Would you like to identify your underlying needs?" help="Underneath every trigger is a need that isn't being met. Naming it is the first step to meeting it." /><YesNo onYes={() => setStep("need")} onNo={() => { skip("needs", "emaSchemas"); goToInsight(); }} /></>);
      case "need":
        return (<><Q tag="Core need" q="Which needs aren't being met?" help="Underneath every trigger is a need. Let AI suggest some from what you've shared, describe them in your own words, or tick every statement that feels true." /><NeedAssistant context={fullCtx({ moodNow, moodNote: moodNote.trim() })} value={needs} onChange={setNeeds} /><NeedPicker value={needs} onChange={setNeeds} /></>);
      case "recognize":
        return (
          <>
            <Q tag="Recognise the schema" />
            {loading ? <div className="mt-6 flex items-center gap-3 text-t-on2"><Spinner className="text-t-brand" /> Finding the schemas that best fit what you shared…</div> : (
              <><p className="mt-3 text-t-on2">{suggested.length ? "Based on what you shared, these are the schemas most likely to have shown up. Tap any that sound like your inner voice, then choose what you did." : "Does any of these sound like your inner voice? Tap the ones that fit, then choose what you did."}</p>
                <SchemaRecognizer needs={needs} suggested={suggested} reasons={reasons} value={recognized} onChange={setRecognized} /></>
            )}
          </>
        );
      case "flip":
        return (<><Q tag="Flip the Script" help="Your Healthy Adult plan, moving from the old script to a new one." /><FlipPlan flips={flips} onChange={setFlips} mode={modeState} needs={needs} /></>);
      case "insight":
        return (
          <>
            <Q tag="A reflection for you" />
            {loading ? <div className="mt-6 flex items-center gap-3 text-t-on2"><Spinner className="text-t-brand" /> Gently making sense of what you moved through…</div> : (
              <>
                <div className="mt-4 rounded-[20px] bg-t-brand3 p-5 text-t-on leading-relaxed text-[16px] whitespace-pre-line" data-testid="ai-insight"><NoteHeader className="mb-3" title={<p className="flex items-center gap-2 font-semibold text-t-onbrand3"><Sparkles size={16} /> Your reflection</p>} text={insight} testId="insight-listen" />{insight}<AiNoteActions text={insight} kind="insight" sessionId={id} testId="insight" /></div>
                {needs.some((n) => n.includes(":")) ? <div className="mt-6"><NeedIdeas needs={needs} context={fullCtx({ moodNow, moodNote: moodNote.trim() })} value={needIdeas} onChange={setNeedIdeas} /></div> : null}
                <p className="tf-tag mt-8">Your reflections</p>
                <p className="mt-1 text-sm text-t-muted">Add anything you'd like to remember — in your own words. Optional.</p>
                <textarea className="tf-input mt-3" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What stood out, what you want to carry forward…" data-testid="reflection-notes-input" />
              </>
            )}
          </>
        );
      case "coping":
        return (<><Q q="What helped you cope?" help="Tag every strategy you used, or add your own." /><div className="mt-6"><CopingSelector value={coping} onChange={setCoping} /></div></>);
      default:
        return <Spinner className="text-t-brand" />;
    }
  };

  const footer = () => {
    const B = (label, onClick, testId, extra = {}) => <Btn className="w-full" label={label} onClick={onClick} testId={testId} {...extra} />;
    const busy = { loading: update.isPending };
    if (only) {
      switch (step) {
        case "moderesult": return B("Save section", () => saveOnly({ copingMode: modeState, survivalResponse: response }), "only-save-button", busy);
        case "soothe": return B("Save section", () => saveOnly({ schemaState }), "only-save-button", busy);
        case "need": { const real = needs.filter((n) => n !== NEED_UNSURE); return B("Save section", () => saveOnly({ needs: real }, real.length ? [] : ["needs"]), "only-save-button", busy); }
        case "flip": return B("Save section", () => saveOnly({ emaSchemas }), "only-save-button", busy);
        case "insight": return B("Save section", () => saveOnly({ aiInsight: insight, needIdeas, reflectionNotes: notes.trim() || null }), "only-save-button", { ...busy, disabled: loading });
        default: break;
      }
    }
    switch (step) {
      case "post": return B("Continue", () => setStep("triggered"), "continue-button", { disabled: postSuds === null });
      case "moodbefore": return B("Continue", () => setStep("trignote"), "moodbefore-continue-button", { disabled: moodBefore === null });
      case "trignote": return B(triggerNote.trim() ? "Continue" : "Skip", () => { if (!triggerNote.trim()) skip("triggerNote"); setStep("identify"); }, "trignote-continue-button");
      case "moderesult": return B("Continue", startSchema, "moderesult-continue-button");
      case "soothe": return B("Continue", startValidation, "soothe-continue-button");
      case "moodsummary": return B("Continue", () => setStep("moodnow"), "moodsummary-continue-button", { disabled: loading });
      case "need": return B(needs.length ? "Continue" : "Skip", goToRecognize, "need-continue-button");
      case "recognize": return B(recognized.length ? "Flip the script" : "Skip", afterRecognize, "recognize-continue-button", { disabled: loading });
      case "flip": return B("Continue", goToInsight, "flip-continue-button");
      case "insight": return B("Continue", () => setStep("coping"), "insight-continue-button", { disabled: loading });
      case "coping": return B("Save & close", () => finish(), "coping-continue-button", { loading: update.isPending });
      case "moodnow":
        if (moodNow !== null && moodNow < LOW_MOOD && moodBefore === null)
          return (
            <div className="flex flex-col gap-3">
              <p className="text-center text-t-on2" data-testid="low-mood-message">That's a heavy feeling. Let's interrupt it together with a short calming exercise.</p>
              {B("Get help now", getHelpForMood, "mood-get-help-button", { style: { background: "var(--hot)", color: "#fff" } })}
              {B("Just save my mood", finishOrNeeds, "save-entry-button", { variant: "ghost" })}
            </div>
          );
        return B(triggered ? "Continue" : "Save & close", finishOrNeeds, "save-entry-button", { disabled: moodNow === null, loading: update.isPending });
      default: return null;
    }
  };

  return (
    <div className="min-h-screen">
      <Page className="!pb-48">
        <BrandHeader right={<CloseBtn onClick={() => (only ? navigate(`/entry/${id}`, { replace: true }) : finish(false))} testId="reflection-close" />} />
        <div className="mt-10 tf-rise" key={step + schemaNodeId + modeNodeId}>{body()}</div>
      </Page>
      <div className="fixed bottom-0 inset-x-0 bg-t-surface/90 backdrop-blur-xl border-t border-t-border">
        <div className="mx-auto max-w-2xl px-5 sm:px-8 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">{footer()}</div>
      </div>
    </div>
  );
}
